import React, {
	createContext,
	type Dispatch,
	type ReactNode,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import { initialState } from './constants';
import type { Panel, PreviewPanelsAction, PreviewPanelsApiState } from './types';

export const PreviewPanelsContext = createContext<
	| {
			state: PreviewPanelsApiState;
			dispatch: Dispatch<PreviewPanelsAction>;
	  }
	| undefined
>(undefined);

export const previewPanelsReducer = (
	state: PreviewPanelsApiState,
	action: PreviewPanelsAction,
): PreviewPanelsApiState => {
	switch (action.type) {
		case 'SET_PANEL_WIDTH':
			return { ...state, width: action.width };
		case 'CLOSE':
			// Action explicitly called in order to close the active panel.
			action.onClose?.();
			state.closePanelSlot?.();
			return initialState;
		case 'OPEN':
			// Action explicitly called in order to open a new panel.
			const { params } = action;

			// If the panel is already open, do nothing.
			if (state.panel?.id === params.panel.id) {
				return state;
			}

			// Close the active panel.
			state.onClose();
			state.closePanelSlot?.();

			// Open the new panel.
			state.openPanelSlot?.();
			params.onOpen?.();
			return {
				...state,
				panel: params.panel,
				width: params.width || initialState.width,
				onOpen: params.onOpen || initialState.onOpen,
				onClose: params.onClose || initialState.onClose,
			};
		default:
			return state;
	}
};

export const PreviewPanelsProvider = ({
	onPreviewPanelOpen,
	onPreviewPanelClose,
	children,
}: {
	children: ReactNode;
	onPreviewPanelOpen?: () => void;
	onPreviewPanelClose?: () => void;
}) => {
	initialState.closePanelSlot = onPreviewPanelClose;
	initialState.openPanelSlot = onPreviewPanelOpen;

	const [state, dispatch] = useReducer(previewPanelsReducer, initialState);
	const context = useMemo(
		() => ({
			state,
			dispatch,
		}),
		[state, dispatch],
	);

	return <PreviewPanelsContext.Provider value={context}>{children}</PreviewPanelsContext.Provider>;
};

// Hook to use the context
export const usePreviewPanelsContext = () => {
	const context = useContext(PreviewPanelsContext);
	return context;
};

// Context selectors
export const useIsPreviewPanelOpen = (panelId?: string): boolean => {
	const context = usePreviewPanelsContext();
	return panelId ? context?.state.panel?.id === panelId : context?.state.panel != null;
};

export const useCurrentPreviewPanel = (): (Panel & { width?: number }) | null => {
	const context = usePreviewPanelsContext();

	if (context?.state.panel == null) {
		return null;
	}

	return { ...context.state.panel, width: context.state.width };
};
